<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// import { navigateToSetting } from 'src/stores/ui/navigate-to';
	//Components
	import ButtonType from './ButtonType.svelte';
	import ButtonModeOptionsG2RGBW from './ButtonModeOptionsG2RGBW.svelte';
	import InitialStateOptions from './InitialStateOptions.svelte';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		isXMODDevice,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	import { compare2Versions } from 'src/helpers/firmware_compare_helpers';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../../services/gen_2_http_reqs';
	//Types
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	//Design system components
	import { Alert, Button, Container, Select, toast } from '@Shelly/shelly-design-system';
	//Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';

	$: statusStore = getDeviceStatus(id);
	export let device,
		isOffline = false;

	/** When unset, XMOD (S3MX-0A) hides the per-input channel selector. */
	export let hideChannelSelector: boolean | undefined;
	/** When unset, XMOD omits analog input type (button/switch only). */
	export let hideAnalogInputType: boolean | undefined;
	/** When unset, XMOD omits the "Activation switch" output mode. */
	export let hideActivateButtonMode: boolean | undefined;

	const id = real_id(device.id);
	const isXmod = isXMODDevice(device?.type);

	$: effectiveHideChannelSelector = hideChannelSelector ?? isXmod;
	$: effectiveHideAnalogInputType = hideAnalogInputType ?? isXmod;
	$: effectiveHideActivateButtonMode = hideActivateButtonMode ?? isXmod;

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	let inputsSettings = [];
	let typeValue;
	let profile;
	let cacheTypeValue;
	let modeValue;
	let channel;
	let component;
	let cacheModeValue;
	let initialStateValue;
	let cacheInitialStateValue;
	let channelValue;

	let loading = false;

	$: isRgbwWhiteChannelInput = profile === 'rgbw' && channel === 2;

	const firmWareVersion = settings?.sys?.device.fw_id || settings?.DeviceInfo?.fw_id;
	const mandatoryUpdate = compare2Versions(firmWareVersion, '1.3.3') === 1;

	$: options = effectiveHideAnalogInputType ? ['button', 'switch'] : ['button', 'switch', 'analog'];
	$: _options = effectiveHideAnalogInputType
		? [$format('working_type_button'), $format('working_type_switch')]
		: [
				$format('working_type_button'),
				$format('working_type_switch'),
				$format('working_type_analog', { default: 'Analog' }),
			];
	$: buttonModeOptions =
		typeValue !== 'analog' || effectiveHideAnalogInputType
			? {
					follow: {
						type: 'switch',
						text: $format('btn_mode_follow_description'),
						title: $format('btn_mode_follow_title', { default: 'Toggle switch' }),
						description: $format('btn_mode_follow_desc', {
							default:
								'Act as a flip switch with one state for "ON" and one state for "OFF".',
						}),
					},
					flip: {
						type: 'switch',
						text: $format('btn_mode_flip_description'),
						title: $format('btn_mode_flip_title', { default: 'Edge switch' }),
						description: $format('btn_mode_flip_desc', {
							default: 'Changes state on every hit.',
						}),
					},
					detached: {
						type: 'any',
						text: $format('btn_mode_detached_description'),
						title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
						description: $format('btn_mode_detached_desc', {
							default: 'Switch is separated from the relays.',
						}),
					},
					...(effectiveHideActivateButtonMode
						? {}
						: {
								activate: {
									type: 'any',
									text: $format('btn_mode_activate_description', {
										default:
											'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
									}),
									title: $format('btn_mode_activate_title', {
										default: 'Activation switch',
									}),
									description: $format('btn_mode_activate_desc', {
										default:
											'Used with motion sensor. Any input turns ON and resets Auto Off timer.',
									}),
								},
							}),
					dim: {
						type: 'button',
						text: $format('btn_mode_dimming_description', {
							default:
								'One button dimming control - Push to toggle, hold to dim up/down (changes direction on each hold)',
						}),
						title: $format('btn_mode_dimming_title', {
							default: 'One button dimming control',
						}),
						description: $format('btn_mode_dimming_desc', {
							default: 'Push to toggle, hold to dim up/down.',
						}),
					},
					dual_dim:
						profile !== 'light'
							? {
									type: 'button',
									text: $format('btn_mode_dual_dim_description', {
										default:
											'Dual button dim mode - 2 inputs configuration (buttons) for up down',
									}),
									title: $format('btn_mode_dual_dim_title', {
										default: 'Dual button dim mode',
									}),
									description: $format('btn_mode_dual_dim_desc', {
										default: '2 inputs configuration (buttons) for up/down.',
									}),
								}
							: undefined,
				}
			: {
					follow: {
						type: 'analog',
						text: $format('btn_mode_analog_description', {
							default: 'Analog (follow) - Brightness is controlled via the analog input',
						}),
						title: $format('btn_mode_analog_title', { default: 'Analog follow' }),
						description: $format('btn_mode_analog_desc', {
							default: 'Brightness is controlled via the analog input.',
						}),
					},
					flip: {
						type: 'switch',
						text: $format('btn_mode_flip_description'),
						title: $format('btn_mode_flip_title', { default: 'Edge switch' }),
						description: $format('btn_mode_flip_desc', {
							default: 'Changes state on every hit.',
						}),
					},
					detached: {
						type: 'analog',
						text: $format('btn_mode_detached_description'),
						title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
						description: $format('btn_mode_detached_desc', {
							default: 'Switch is separated from the relays.',
						}),
					},
					...(effectiveHideActivateButtonMode
						? {}
						: {
								activate: {
									type: 'switch',
									text: $format('btn_mode_activate_description', {
										default:
											'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
									}),
									title: $format('btn_mode_activate_title', {
										default: 'Activation switch',
									}),
									description: $format('btn_mode_activate_desc', {
										default:
											'Used with motion sensor. Any input turns ON and resets Auto Off timer.',
									}),
								},
							}),
					dim: {
						type: 'button',
						text: $format('btn_mode_dimming_description', {
							default:
								'One button dimming control - Push to toggle, hold to dim up/down (changes direction on each hold)',
						}),
						title: $format('btn_mode_dimming_title', {
							default: 'One button dimming control',
						}),
						description: $format('btn_mode_dimming_desc', {
							default: 'Push to toggle, hold to dim up/down.',
						}),
					},
					dual_dim:
						profile !== 'light'
							? {
									type: 'button',
									text: $format('btn_mode_dual_dim_description', {
										default:
											'Dual button dim mode - 2 inputs configuration (buttons) for up down',
									}),
									title: $format('btn_mode_dual_dim_title', {
										default: 'Dual button dim mode',
									}),
									description: $format('btn_mode_dual_dim_desc', {
										default: '2 inputs configuration (buttons) for up/down.',
									}),
								}
							: undefined,
				};
	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description'),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description'),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description'),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
		...(isXmod
			? {
					match_input: {
						type: 'switch',
						text: $format('init_state_match_input_description'),
						title: $format('match_input', { default: 'Current state of the switch' }),
						description: $format('init_state_match_input_desc', {
							default:
								'Operates the device according to the state of the switch or button connected to the device.',
						}),
					},
					toggle: {
						type: 'any',
						text: $format('init_state_toggle_description', {
							default: 'Toggles the output when power is restored.',
						}),
						title: $format('init_state_toggle_title', { default: 'Toggle' }),
						description: $format('init_state_toggle_desc', {
							default: 'Toggles the output when power is restored.',
						}),
					},
				}
			: {}),
	};

	const _initialStateOptionsScc = {
		on: $format('init_state_on_scc_toast', { default: 'on' }),
		off: $format('init_state_off_scc_toast', { default: 'off' }),
		restore_last: $format('init_state_restore_last_scc_toast', {
			default: 'restore last',
		}),
		...(isXmod
			? {
					match_input: $format('init_state_match_input_scc_toast', {
						default: 'Match input',
					}),
					toggle: $format('init_state_toggle_scc_toast', { default: 'Toggle' }),
				}
			: {}),
	};

	const setTypeValue = async (ch?: number) => {
		const res_input: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
			id: ch || channel,
			config: { type: typeValue },
		});

		if (res_input.isok) {
			if (!ch) {
				toast.open({
					type: 'success',
					title: $format('input_type_scc_toast', {
						values: { mode_value: _options[options.indexOf(typeValue)] },
					}),
				});
			}
			settings[`input:${ch || channel}`].type = typeValue;
			devices_settings.update_devices_settings(id, settings);
			cacheTypeValue = typeValue;
			// navigateToSetting(device.id, 'settings', 'input_output'); //navigating to input_output because dynamic addition/removal of settings
		} else {
			typeValue = cacheTypeValue;
			toast.open({ type: 'danger', title: $format('btn_type_err_toast') });
		}
		return res_input;
	};

	const setModeAndInitialOptions = async () => {
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: channel,
				config: { in_mode: modeValue, initial_state: initialStateValue },
			},
		);

		if (res.isok) {
			if (cacheModeValue !== modeValue) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: modeValue },
					}),
				});
				cacheModeValue = modeValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
				devices_settings.update_devices_settings(id, settings);
			}

			if (cacheInitialStateValue !== initialStateValue) {
				toast.open({
					type: 'success',
					title: $format('init_state_scc_toast', {
						values: {
							initial_state_value: _initialStateOptionsScc[initialStateValue],
						},
					}),
				});
				cacheInitialStateValue = initialStateValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].initial_state =
					initialStateValue;

				devices_settings.update_devices_settings(id, settings);
			}
		} else {
			if (cacheModeValue !== modeValue) {
				modeValue = cacheModeValue;
				toast.open({ type: 'danger', title: $format('btn_mode_err_toast') });
			}
			if (cacheInitialStateValue !== initialStateValue) {
				initialStateValue = cacheInitialStateValue;
				toast.open({ type: 'danger', title: $format('init_state_err_toast') });
			}
		}
		return res;
	};

	const setButtonTypeInputs = async () => {
		const method = 'input.setconfig';
		const params = { id: channel, config: { type: typeValue } };

		const res: SettingRequest = await gen2Request(device, method, params);

		if (res.isok) {
			settings[`input:${channel}`].type = typeValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('btn_type_scc_toast', {
					values: { type_value: typeValue },
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({ type: 'danger', title: $format('btn_type_err_toast') });
		}
		return res;
	};

	const sentRequests = async () => {
		loading = true;
		if (device.category !== 'inputs_reader') {
			if (cacheTypeValue !== typeValue) {
				await setTypeValue();
				if (get_gen_2_component_from_category(device) === 'rgbw' && channel === 0) {
					await setTypeValue(2);
				}
			}
			if (channel === 0 || profile === 'light') {
				if (cacheModeValue !== modeValue || cacheInitialStateValue !== initialStateValue) {
					await setModeAndInitialOptions();
				}
			}
		} else {
			if (cacheTypeValue !== typeValue) {
				await setButtonTypeInputs();
			}
		}
		loading = false;
	};

	const getSettings = () => {
		const mode = get_mode(device, $statusStore, $settingsStore);
		const comPath = mode !== 'light' ? `${mode}:0` : `${mode}:${channel}`;
		channelValue = {
			title: channel,
			value: channel,
		};

		component = `input:${channel}`;
		typeValue = settings?.[component]?.type;
		if (effectiveHideAnalogInputType && typeValue === 'analog') {
			typeValue = 'button';
		}
		profile = settings?.sys?.device?.profile;
		cacheTypeValue = typeValue;
		modeValue = settings[comPath].in_mode || '';
		if (effectiveHideActivateButtonMode && modeValue === 'activate') {
			modeValue = 'follow';
		}
		cacheModeValue = modeValue;
		initialStateValue = settings[comPath].initial_state || '';
		cacheInitialStateValue = initialStateValue;
	};
	onMount(() => {
		channel = device.channel;
		getSettings();
		Object.keys(settings).forEach((setting, i) => {
			if (setting.includes('input:') && Number(setting.split(':')[1]) < 100) {
				inputsSettings.push({
					title: +setting.split(':')[1],
					value: +setting.split(':')[1],
				});
			}
		});
		inputsSettings = inputsSettings.sort((a, b) => a.title - b.title);
	});

	beforeUpdate(() => {
		Object.keys(buttonModeOptions).forEach((key) => {
			if (typeof buttonModeOptions[key] === 'undefined') {
				delete buttonModeOptions[key];
			}
		});
	});
	const setChannel = (e) => {
		channel = typeof e.detail.value !== 'undefined' ? e.detail.value : device.channel;
		channelValue = {
			title: channel,
			value: channel,
		};
		getSettings();
	};
</script>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		{#if !effectiveHideChannelSelector}
			<Select
				class="mb-m"
				icon="fas fa-hashtag"
				options={inputsSettings}
				value={channelValue}
				on:change={setChannel}
				id="select-cyirsk3"
				aria-label={$format('title_select_input', { default: 'Select input' })}
			/>
		{/if}
		{#if isRgbwWhiteChannelInput}
			<Alert
				type="info"
				heading={$format('rgbw_input2_white_channel_heading', {
					default: 'White channel input',
				})}
				message={$format('rgbw_input2_white_channel_message', {
					default:
						'In RGBW mode, Input 2 is reserved for the white channel and shares its configuration with Input 0. To change its settings, select Input 0.',
				})}
			/>
		{:else}
			<ButtonType bind:typeValue {options} {_options} />
			{#if channel === 0 || profile === 'light'}
				<hr class="mb-m mt-s" />
				<ButtonModeOptionsG2RGBW {typeValue} bind:modeValue {buttonModeOptions} />
				<InitialStateOptions
					{typeValue}
					bind:initialStateValue
					{initialStateOptions}
					{modeValue}
					{mandatoryUpdate}
					matchInputOnlyOnSwitchFollow={isXmod}
				/>
			{/if}
		{/if}
	</Container>
	{#if !isRgbwWhiteChannelInput}
		<div class="mb-s">
			<Button class="mt-l" {loading} size="m" width="full" on:click={sentRequests}>
				{$format('button_save')}
			</Button>
		</div>
	{/if}
{:else}
	{#if !effectiveHideChannelSelector}
		<Select
			class="mb-m"
			icon="fas fa-hashtag"
			options={inputsSettings}
			value={channelValue}
			on:change={setChannel}
			id="select-cyirsk3"
			aria-label={$format('title_select_input', { default: 'Select input' })}
		/>
	{/if}
	{#if isRgbwWhiteChannelInput}
		<Alert
			type="info"
			heading={$format('rgbw_input2_white_channel_heading', {
				default: 'White channel input',
			})}
			message={$format('rgbw_input2_white_channel_message', {
				default:
					'In RGBW mode, Input 2 is reserved for the white channel and shares its configuration with Input 0. To change its settings, select Input 0.',
			})}
		/>
	{:else}
		<ButtonType bind:typeValue {options} {_options} />
		{#if channel === 0 || profile === 'light'}
			<hr class="mb-m mt-s" />
			<ButtonModeOptionsG2RGBW {typeValue} bind:modeValue {buttonModeOptions} />
			<InitialStateOptions
				{typeValue}
				bind:initialStateValue
				{initialStateOptions}
				{modeValue}
				{mandatoryUpdate}
				matchInputOnlyOnSwitchFollow={isXmod}
			/>
		{/if}
		<Button class="mt-s" {loading} size="m" width="full" on:click={sentRequests}>
			{$format('button_save')}
		</Button>
	{/if}
{/if}
